import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePath } from 'react-router';
import queryString from 'query-string';
import { PATHS } from 'routes/constants';
import {
	getInvoiceListRequest,
	downloadInvoicePdfRequest,
	downloadInvoiceUdrCsvRequest,
	invoiceListExcelExportRequest,
} from 'redux/reducers/invoiceManagement/reducer';
import {
	selectInvoiceListData,
	selectInvoiceListLoading,
	selectInvoiceListPage,
	selectDownloadInvoicePdfLoader,
	selectDownloadInvoiceUdrCsvLoader,
	selectUpdateInvoicePaymentLoader,
	selectUpdateInvoiceCommentLoader,
	selectInvoiceListExcelExportLoader,
} from 'redux/reducers/invoiceManagement/selectors';
import { PAGE_SIZE } from 'services/constants/env';
import { TInvoiceListItem } from 'redux/reducers/invoiceManagement/types';

// Extended type with required id for table compatibility; payment_receipt_url — receipt of the online payment (payment gateway)
type TInvoiceListItemWithId = TInvoiceListItem & { id: number; payment_receipt_url?: string };
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { DATES_ARRAY, DATES_ID } from 'services/constants/dates-constants';
import { TFiltersQ4Us, TOrderBy } from 'redux/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { DOWNLOAD_PAYMENT_RECEIPT, tableStructure } from './tableStructure';
import {
	canHavePaymentReceipt,
	downloadPaymentReceipt,
	usePaymentReceipts,
} from '../../utils/paymentReceipt';
import { getEMerchantRequest } from '../../redux/reducers/monthlyFee/reducer';
import { EasParamsBox } from '../EMManagement/EMManagementId/EasParamsBox';
import { invoiceManagementBoxStructure } from './boxStructure';
import { InvoicePaymentDialog } from './InvoicePaymentDialog';
import { InvoiceCommentDialog } from './InvoiceCommentDialog';
import { saveUrl } from '../../redux/reducers/auth/reducer';
import { getFiltersFromNavigateParams } from '../../utils/FilterParams';

export const InvoiceManagement: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const { page } = queryString.parse(search);
	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
	const [openCommentDialog, setOpenCommentDialog] = useState(false);
	const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
		useState<TInvoiceListItemWithId | null>(null);
	const [selectedInvoiceForComment, setSelectedInvoiceForComment] =
		useState<TInvoiceListItemWithId | null>(null);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TInvoiceListItemWithId>[]) => {
			setCurrentTableStructure(newTableStructure as TTableStructure<keyof TInvoiceListItem>[]);
		},
		[],
	);

	const handleLink = useCallback(
		(invoiceNumber: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(
				generatePath(PATHS.INVOICE_MANAGEMENT_ID_PAGE, { invoiceNumber: String(invoiceNumber) }),
			);
		},
		[dispatch, navigate],
	);

	const invoiceListData = useSelector(selectInvoiceListData);
	const invoiceListLoading = useSelector(selectInvoiceListLoading);
	const invoiceListPage = useSelector(selectInvoiceListPage);
	const downloadInvoicePdfLoader = useSelector(selectDownloadInvoicePdfLoader);
	const downloadInvoiceUdrCsvLoader = useSelector(selectDownloadInvoiceUdrCsvLoader);
	const updateInvoicePaymentLoader = useSelector(selectUpdateInvoicePaymentLoader);
	const updateInvoiceCommentLoader = useSelector(selectUpdateInvoiceCommentLoader);
	const invoiceListExcelExportLoader = useSelector(selectInvoiceListExcelExportLoader);

	// receipts of the paid invoices on the page that were paid online through the payment gateway
	const receiptInvoiceIds = useMemo(
		() =>
			invoiceListData
				.filter((item) => canHavePaymentReceipt(item.payment_status))
				.map((item) => item.invoice_id),
		[invoiceListData],
	);
	const paymentReceipts = usePaymentReceipts(receiptInvoiceIds);

	// Map invoice_id to id for table compatibility
	const invoiceListDataWithId: TInvoiceListItemWithId[] = useMemo(() => {
		return invoiceListData.map((item) => ({
			...item,
			id: item.invoice_id,
			payment_receipt_url: paymentReceipts.get(item.invoice_id),
		}));
	}, [invoiceListData, paymentReceipts]);

	// const navigateParamsWithoutPage = useMemo(() => {

	// 	const acc = tableStructure.reduce((acc, item) => {
	// 		if (!!navigateParams[item.selector] && item.type === ETableStructureType.DATE) {
	// 			if (navigateParams[item.selector] === DATES_ID.CUSTOM_RANGE) {
	// 				if (navigateParams[`${String(item.selector)}StartDate`]) {
	// 					acc.push({
	// 						name: item.selector as string,
	// 						value: navigateParams[`${String(item.selector)}StartDate`] as string,
	// 						comparator: '>=',
	// 					});
	// 				}
	// 				if (navigateParams[`${String(item.selector)}EndDate`]) {
	// 					acc.push({
	// 						name: item.selector as string,
	// 						value: navigateParams[`${String(item.selector)}EndDate`] as string,
	// 						comparator: '<=',
	// 					});
	// 				}
	// 				return acc;
	// 			}
	// 			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
	// 			if (dates?.from) {
	// 				acc.push({
	// 					name: item.selector as string,
	// 					value: dates.from,
	// 					comparator: '>=',
	// 				});
	// 			}
	// 			if (dates?.to) {
	// 				acc.push({
	// 					name: item.selector as string,
	// 					value: dates.to,
	// 					comparator: '<=',
	// 				});
	// 			}
	// 			return acc;
	// 		}
	// 		if (item.selector === 'total_invoice_amount_in_eur') {
	// 			const totalAmount = navigateParams[item.selector];
	// 			if (totalAmount) {
	// 				acc.push({
	// 					name: 'total_invoice_amount_in_eur',
	// 					value: totalAmount as string,
	// 					comparator: '>=',
	// 				});
	// 				acc.push({
	// 					name: 'total_invoice_amount_in_eur',
	// 					value: String(+totalAmount + 0.01),
	// 					comparator: '<=',
	// 				});
	// 			}
	// 			return acc;
	// 		}

	// 		if (navigateParams[item.selector]) {
	// 			acc.push({
	// 				name: item.selector as string,
	// 				value: navigateParams[item.selector] as string,
	// 				comparator: '=',
	// 			});
	// 		}
	// 		return acc;
	// 	}, [] as TFiltersQ4Us);

	// 	if (navigateParams.searchEmId) {
	// 		acc.push({ name: 'em_id', value: navigateParams.searchEmId as string, comparator: '=' });
	// 	}
	// 	if (navigateParams.search) {
	// 		acc.push({
	// 			name: 'invoice_number',
	// 			value: navigateParams.search as string,
	// 			comparator: '=',
	// 		});
	// 	}

	// 	return acc;
	// }, [navigateParams]);

	const navigateParamsWithoutPage = getFiltersFromNavigateParams(
		currentTableStructure,
		navigateParams,
	) as TFiltersQ4Us;
	if (navigateParams['total_invoice_amount_in_eur']) {
		const totalAmount = navigateParams['total_invoice_amount_in_eur'] as string;
		const firstTotalAmountFilter = navigateParamsWithoutPage.find(
			(item) => item.name === 'total_invoice_amount_in_eur',
		);
		if (firstTotalAmountFilter) {
			firstTotalAmountFilter.value = totalAmount;
			firstTotalAmountFilter.comparator = '>=';
		}
		navigateParamsWithoutPage.push({
			name: 'total_invoice_amount_in_eur',
			value: (+totalAmount + 0.01).toFixed(2),
			comparator: '<=',
		});
	}

	if (navigateParams.search) {
		navigateParamsWithoutPage.push({
			name: 'invoice_number',
			value: navigateParams.search as string,
			comparator: '=',
		});
	}

	const orderByNavigateParams = useMemo(() => {
		const orderBy: string[] = [];
		if (navigateParams?.sortIndex) {
			const sortItem = tableStructure[+navigateParams.sortIndex];
			if (sortItem) {
				orderBy.push(
					`${sortItem.selector} ${navigateParams?.sortDirection === 'desc' ? 'desc' : 'asc'}`,
				);
			}
		}
		orderBy.push('timestamp desc');
		orderBy.push('invoice_id asc');

		return orderBy;
	}, [navigateParams]);

	const pageSize = PAGE_SIZE;
	const currentPage = Number(page) || 1;

	const getInvoiceListRequestPayload = useCallback(
		() => ({
			tableName: 'invoice_list_info',
			orderBy: orderByNavigateParams.length ? orderByNavigateParams : ['timestamp desc'],
			filter: navigateParamsWithoutPage,
			page: { size: pageSize, from: (currentPage - 1) * pageSize },
		}),
		[orderByNavigateParams, navigateParamsWithoutPage, pageSize, currentPage],
	);

	useEffect(() => {
		dispatch(getInvoiceListRequest(getInvoiceListRequestPayload()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	const handleAction = useCallback(
		(id: number, typeOfDialog: string) => {
			const row = invoiceListDataWithId.find((item) => item.id === id);
			if (!row) return;

			if (typeOfDialog === 'Download invoice in PDF') {
				dispatch(
					downloadInvoicePdfRequest({
						emId: row.em_id,
						invoiceId: row.invoice_id,
					}),
				);
			} else if (typeOfDialog === 'Download invoice in CSV') {
				dispatch(
					downloadInvoiceUdrCsvRequest({
						emId: row.em_id,
						invoiceId: row.invoice_id,
					}),
				);
			} else if (typeOfDialog === 'Mark as paid or edit payment information') {
				setSelectedInvoiceForPayment(row);
				setOpenPaymentDialog(true);
			} else if (typeOfDialog === 'Add or edit comments for the invoice') {
				setSelectedInvoiceForComment(row);
				setOpenCommentDialog(true);
			} else if (typeOfDialog === DOWNLOAD_PAYMENT_RECEIPT && row.payment_receipt_url) {
				downloadPaymentReceipt(row.payment_receipt_url);
			}
		},
		[dispatch, invoiceListDataWithId],
	);

	useEffect(() => {
		dispatch(getEMerchantRequest());
	}, [dispatch]);

	const handleExportToExcel = useCallback(() => {
		dispatch(
			invoiceListExcelExportRequest({
				tableName: 'invoice_list_info',
				filter: navigateParamsWithoutPage,
			}),
		);
	}, [dispatch, navigateParamsWithoutPage]);

	return (
		<Box
			sx={{
				margin: '0px 10px 20px 10px',
			}}
		>
			<Box sx={{ width: 'auto', minWidth: '800px', marginBottom: 1 }}>
				<EasParamsBox<{
					id: number;
					invoices_count: number;
					total_amount_in_eur_invoices: number;
					total_invoiced_amount_in_eur_invoices: number;
					total_paid_amount_in_eur: number;
					balance_in_eur: string;
				}>
					boxStructure={invoiceManagementBoxStructure}
					items={{
						id: 0,
						invoices_count: invoiceListPage?.total || 0,
						total_amount_in_eur_invoices: invoiceListPage?.total_amount_in_eur_invoices || 0,
						total_invoiced_amount_in_eur_invoices:
							invoiceListPage?.total_invoiced_amount_in_eur_invoices || 0,
						total_paid_amount_in_eur: invoiceListPage?.total_paid_amount_in_eur || 0,
						balance_in_eur: invoiceListPage?.balance_in_eur || '0',
					}}
					columnNumber={5}
					editable={false}
					loading={invoiceListLoading}
				/>
			</Box>

			{/* {invoiceListPage && (
				<Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
					<Typography variant="body2">
						<strong>Total Amount (EUR):</strong>{' '}
						{invoiceListPage.total_amount_in_eur_invoices?.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</Typography>
					<Typography variant="body2">
						<strong>Total Invoiced (EUR):</strong>{' '}
						{invoiceListPage.total_invoiced_amount_in_eur_invoices?.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</Typography>
					<Typography variant="body2">
						<strong>Total Paid (EUR):</strong>{' '}
						{invoiceListPage.total_paid_amount_in_eur?.toLocaleString('en-US', {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						})}
					</Typography>
					<Typography variant="body2">
						<strong>Balance (EUR):</strong> {invoiceListPage.balance_in_eur}
					</Typography>
				</Box>
			)} */}
			<EasTableWithFilter<TInvoiceListItemWithId>
				tableStructure={currentTableStructure}
				items={{
					pageNumber: currentPage,
					pageSize: pageSize,
					totalCount: invoiceListPage?.total ?? 0,
					data: invoiceListDataWithId,
				}}
				itemsIsLoading={
					invoiceListLoading ||
					downloadInvoicePdfLoader ||
					downloadInvoiceUdrCsvLoader ||
					updateInvoicePaymentLoader ||
					updateInvoiceCommentLoader
				}
				handleAction={handleAction}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box
					sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.5 }}
				>
					<Typography variant="body1" color="primary" fontWeight="bold">
						Invoice Management
					</Typography>
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={handleExportToExcel}
						disabled={invoiceListExcelExportLoader || invoiceListLoading}
					>
						Export to Excel
					</Button>
				</Box>
			</EasTableWithFilter>
			<InvoicePaymentDialog
				open={openPaymentDialog}
				setClose={() => {
					setOpenPaymentDialog(false);
					setSelectedInvoiceForPayment(null);
				}}
				data={selectedInvoiceForPayment}
				invoiceListRequestPayload={getInvoiceListRequestPayload()}
			/>
			<InvoiceCommentDialog
				open={openCommentDialog}
				setClose={() => {
					setOpenCommentDialog(false);
					setSelectedInvoiceForComment(null);
				}}
				data={selectedInvoiceForComment}
				invoiceListRequestPayload={getInvoiceListRequestPayload()}
			/>
		</Box>
	);
};
